"use client";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { AttendeeType } from "@/hooks/useSignupFunnel";
import { useTranslations } from "next-intl";
import Image from "next/image";

interface SelectAttendeeStepProps {
  /** Chosen attendee branch — routes to the matching details step. */
  onSelect: (attendeeType: AttendeeType) => void;
  allowed?: AttendeeType[];
}

interface AttendeeCardProps {
  imageSrc: string;
  title: string;
  description: string;
  selectLabel: string;
  selectText: string;
  onSelect: () => void;
}

function AttendeeCard({
  imageSrc,
  title,
  description,
  selectLabel,
  selectText,
  onSelect,
}: AttendeeCardProps) {
  return (
    <Card className="items-center gap-3 px-6 py-6 text-center">
      <Image
        src={imageSrc}
        alt=""
        width={80}
        height={80}
        className="h-20 w-20 object-cover"
      />
      <div className="flex flex-col gap-1">
        <p className="font-semibold">{title}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <Button
        variant="default"
        className="w-full rounded-full"
        aria-label={selectLabel}
        onClick={onSelect}
      >
        {selectText}
      </Button>
    </Card>
  );
}

/**
 * Presentational: it owns no funnel state, only reports the choice upward.
 *
 * Keys are referenced as literals (not built dynamically) so the i18n
 * usage check can statically see them.
 */
export default function SelectAttendeeStep({
  onSelect,
  allowed = ["SELF", "CHILD"],
}: SelectAttendeeStepProps) {
  const t = useTranslations("publicEventSignup");
  const selectText = t("selectAttendee.select");

  return (
    <div className="flex flex-1 flex-col gap-4">
      <h2 className="text-lg font-semibold">{t("steps.selectAttendee")}</h2>
      {allowed.includes("SELF") && (
        <AttendeeCard
          imageSrc="/signup-attendee-myself.png"
          title={t("selectAttendee.myselfTitle")}
          description={t("selectAttendee.myselfDescription")}
          selectLabel={t("selectAttendee.selectLabel", {
            attendee: t("selectAttendee.myselfTitle"),
          })}
          selectText={selectText}
          onSelect={() => onSelect("SELF")}
        />
      )}
      {allowed.includes("CHILD") && (
        <AttendeeCard
          imageSrc="/signup-attendee-child.png"
          title={t("selectAttendee.childTitle")}
          description={t("selectAttendee.childDescription")}
          selectLabel={t("selectAttendee.selectLabel", {
            attendee: t("selectAttendee.childTitle"),
          })}
          selectText={selectText}
          onSelect={() => onSelect("CHILD")}
        />
      )}
    </div>
  );
}
